import { useEffect } from "react";
import { useGame } from "../stores/game";

/**
 * Reactions floating up over the reveal. plan.md §12: transient, never
 * persisted, random placement, reduced-motion respected.
 *
 * Each one owns its own timer, in its own component. The previous version ran a
 * single effect over the whole array, so every new reaction tore down and
 * recreated the timers of the ones already floating — with a steady stream of
 * taps nothing ever reached its own deadline, they piled up and then vanished
 * together. Per-item state is what makes each lifetime independent.
 *
 * Placement is random per reaction and decided in the store when it arrives,
 * scattered across the bottom third. Deriving it from the id produced a
 * marching pattern rather than a scatter, and deriving it at render made each
 * one jump whenever the list changed.
 *
 * The timer, not `animationend`, is what removes them: the reduced-motion rule
 * in `index.css` collapses animations to ~0ms, and an `animationend` listener
 * would clear the whole batch instantly.
 */

const LIFETIME_MS = 2_400;

const Floater = ({
  id,
  emoji,
  x,
  y,
}: {
  id: number;
  emoji: string;
  x: number;
  y: number;
}) => {
  const dismiss = useGame((s) => s.dismissReaction);

  useEffect(() => {
    const timer = setTimeout(() => dismiss(id), LIFETIME_MS);
    return () => clearTimeout(timer);
  }, [id, dismiss]);

  return (
    <span
      className="absolute select-none text-3xl"
      style={{
        left: `${x}%`,
        bottom: `${y}%`,
        animation: `badorbad-float ${LIFETIME_MS}ms ease-out forwards`,
      }}
    >
      {emoji}
    </span>
  );
};

export const Reactions = () => {
  const reactions = useGame((s) => s.reactions);

  if (reactions.length === 0) return null;

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 z-20 overflow-hidden"
    >
      {reactions.map((r) => (
        <Floater key={r.id} id={r.id} emoji={r.emoji} x={r.x} y={r.y} />
      ))}
    </div>
  );
};
